import React, { useState, useEffect } from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import { Calendar, Plus, Clock, MapPin, Users, Check, X, ChevronDown, ChevronUp, UserCircle } from 'lucide-react'

const API_URL = process.env.REACT_APP_BACKEND_URL || ''

export default function PortalSchedule() {
  const { team, userRole, canManage, token } = useOutletContext()
  const navigate = useNavigate()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddEvent, setShowAddEvent] = useState(false)
  const [expandedEvent, setExpandedEvent] = useState(null)
  const [eventRsvps, setEventRsvps] = useState({})
  const [loadingRsvps, setLoadingRsvps] = useState({})
  const [newEvent, setNewEvent] = useState({
    title: '',
    event_type: 'practice',
    start_time: '',
    end_time: '',
    location: '',
    notes: ''
  })
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    if (team?.team_id) {
      fetchEvents()
    }
  }, [team?.team_id])

  const fetchEvents = async () => {
    try {
      const res = await fetch(`${API_URL}/api/teams/${team.team_id}/events`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Environment': 'production'
        }
      })
      if (res.ok) {
        const data = await res.json()
        setEvents(data || [])
      }
    } catch (err) {
      console.error('Error fetching events:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchEventRsvps = async (eventId) => {
    setLoadingRsvps(prev => ({ ...prev, [eventId]: true }))
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/rsvps`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Environment': 'production'
        }
      })
      if (res.ok) {
        const data = await res.json()
        setEventRsvps(prev => ({ ...prev, [eventId]: data }))
      }
    } catch (err) {
      console.error('Error fetching RSVPs:', err)
    } finally {
      setLoadingRsvps(prev => ({ ...prev, [eventId]: false }))
    }
  }

  const toggleEventExpand = (eventId) => {
    if (expandedEvent === eventId) {
      setExpandedEvent(null)
    } else {
      setExpandedEvent(eventId)
      if (!eventRsvps[eventId]) {
        fetchEventRsvps(eventId)
      }
    }
  }

  const handleCreateEvent = async (e) => {
    e.preventDefault()
    if (!newEvent.title || !newEvent.start_time) {
      alert('Please fill in required fields')
      return
    }

    setCreating(true)
    try {
      const res = await fetch(`${API_URL}/api/events`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Environment': 'production'
        },
        body: JSON.stringify({
          ...newEvent,
          team_id: team.team_id
        })
      })

      if (res.ok) {
        setShowAddEvent(false)
        setNewEvent({
          title: '',
          event_type: 'practice',
          start_time: '',
          end_time: '',
          location: '',
          notes: ''
        })
        fetchEvents()
      } else {
        const error = await res.json()
        alert(error.detail || 'Failed to create event')
      }
    } catch (err) {
      console.error('Error creating event:', err)
      alert('Failed to create event')
    } finally {
      setCreating(false)
    }
  }

  const handleRSVP = async (eventId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/rsvp`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Environment': 'production'
        },
        body: JSON.stringify({ status })
      })

      if (res.ok) {
        fetchEvents()
        // Refresh RSVPs if this event is expanded
        if (expandedEvent === eventId) {
          fetchEventRsvps(eventId)
        }
      }
    } catch (err) {
      console.error('Error updating RSVP:', err)
    }
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatTime = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit'
    })
  }

  // Group events by date
  const groupedEvents = events.reduce((groups, event) => {
    const date = new Date(event.event_date || event.start_time || event.date).toDateString()
    if (!groups[date]) {
      groups[date] = []
    }
    groups[date].push(event)
    return groups
  }, {})

  const RsvpList = ({ eventId }) => {
    const rsvpData = eventRsvps[eventId]
    const isLoading = loadingRsvps[eventId]

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black"></div>
        </div>
      )
    }

    if (!rsvpData) return null

    const { going, maybe, not_going, no_response, counts } = rsvpData

    const RsvpGroup = ({ title, items, bgColor, textColor, emptyText }) => (
      <div className="mb-4">
        <h4 className={`text-sm font-semibold ${textColor} mb-2`}>
          {title} ({items.length})
        </h4>
        {items.length === 0 ? (
          <p className="text-sm text-zinc-500 italic">{emptyText}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {items.map((item, idx) => (
              <div 
                key={idx} 
                className={`flex items-center gap-2 ${bgColor} rounded-lg px-3 py-2`}
              >
                <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center overflow-hidden">
                  {item.user_picture ? (
                    <img src={item.user_picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <UserCircle className="w-5 h-5 text-zinc-500" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-white">{item.user_name}</p>
                  {item.parent_name && (
                    <p className="text-xs text-zinc-400">via {item.parent_name}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )

    return (
      <div className="mt-4 pt-4 border-t border-zinc-700">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <RsvpGroup 
              title="Going" 
              items={going} 
              bgColor="bg-green-500/10" 
              textColor="text-green-400"
              emptyText="No responses yet"
            />
            <RsvpGroup 
              title="Maybe" 
              items={maybe} 
              bgColor="bg-yellow-500/10" 
              textColor="text-yellow-400"
              emptyText="None"
            />
          </div>
          <div>
            <RsvpGroup 
              title="Can't Go" 
              items={not_going} 
              bgColor="bg-red-500/10" 
              textColor="text-red-400"
              emptyText="None"
            />
            <RsvpGroup 
              title="No Response" 
              items={no_response} 
              bgColor="bg-zinc-900" 
              textColor="text-zinc-300"
              emptyText="Everyone has responded!"
            />
          </div>
        </div>
        
        {/* Summary */}
        <div className="mt-4 pt-4 border-t border-zinc-700 text-sm text-zinc-400">
          Total: {counts?.going || 0} going, {counts?.maybe || 0} maybe, {counts?.not_going || 0} can't go, {counts?.no_response || 0} no response
          {counts?.total_players && <span> &middot; {counts.total_players} players on team</span>}
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Schedule</h1>
          <p className="text-zinc-400 mt-1">{events.length} events</p>
        </div>
        
        <div className="flex items-center gap-3">
          {canManage && (
            <button
              onClick={() => setShowAddEvent(true)}
              className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-xl font-medium hover:bg-zinc-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Event
            </button>
          )}
        </div>
      </div>

      {/* Events List */}
      {events.length === 0 ? (
        <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-12 text-center">
          <Calendar className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No Events Scheduled</h3>
          <p className="text-zinc-400 mb-6">Get started by adding your first event.</p>
          {canManage && (
            <button
              onClick={() => setShowAddEvent(true)}
              className="px-6 py-3 bg-black text-white rounded-xl font-medium hover:bg-zinc-700 transition-colors"
            >
              Add Event
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedEvents).map(([dateStr, dateEvents]) => (
            <div key={dateStr}>
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-3">
                {formatDate(dateStr)}
              </h3>
              <div className="space-y-3">
                {dateEvents.map(event => {
                  const isExpanded = expandedEvent === event.event_id
                  const rsvpData = eventRsvps[event.event_id]
                  const goingCount = rsvpData?.counts?.going || event.rsvp_counts?.going || 0
                  const totalPlayers = rsvpData?.counts?.total_players || 0
                  
                  return (
                    <div key={event.event_id} className="bg-zinc-800 rounded-xl border border-zinc-700 overflow-hidden hover:shadow-md transition-shadow">
                      <div className="p-4">
                        <div className="flex items-start gap-4">
                          {/* Event Type Badge */}
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            event.event_type === 'game' ? 'bg-red-500/20' :
                            event.event_type === 'practice' ? 'bg-blue-500/20' :
                            event.event_type === 'meeting' ? 'bg-purple-500/20' :
                            'bg-zinc-800'
                          }`}>
                            <Calendar className={`w-6 h-6 ${
                              event.event_type === 'game' ? 'text-red-400' :
                              event.event_type === 'practice' ? 'text-blue-400' :
                              event.event_type === 'meeting' ? 'text-purple-400' :
                              'text-zinc-300'
                            }`} />
                          </div>
                          
                          {/* Event Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <h4 className="font-semibold text-white">{event.title || event.name}</h4>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-zinc-400">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {formatTime(event.event_date || event.start_time || event.date)}
                                    {event.end_date && ` - ${formatTime(event.end_date)}`}
                                  </span>
                                  {event.location && (
                                    <span className="flex items-center gap-1">
                                      <MapPin className="w-4 h-4" />
                                      {event.location}
                                    </span>
                                  )}
                                </div>
                                {event.notes && (
                                  <p className="text-sm text-zinc-400 mt-2">{event.notes}</p>
                                )}
                              </div>
                              
                              {/* Event Type */}
                              <span className={`px-3 py-1 text-xs font-medium rounded-full flex-shrink-0 ${
                                event.event_type === 'game' ? 'bg-red-500/20 text-red-400' :
                                event.event_type === 'practice' ? 'bg-blue-500/20 text-blue-400' :
                                event.event_type === 'meeting' ? 'bg-purple-500/20 text-purple-400' :
                                'bg-zinc-800 text-zinc-200'
                              }`}>
                                {event.event_type || 'Event'}
                              </span>
                            </div>
                            
                            {/* RSVP Section */}
                            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-zinc-700">
                              {/* Attendance Summary */}
                              <button
                                onClick={() => toggleEventExpand(event.event_id)}
                                className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors"
                              >
                                <Users className="w-4 h-4" />
                                <span className="font-medium text-green-600">{goingCount} going</span>
                                {totalPlayers > 0 && (
                                  <span className="text-zinc-500">/ {totalPlayers} players</span>
                                )}
                                {isExpanded ? (
                                  <ChevronUp className="w-4 h-4 text-zinc-500" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-zinc-500" />
                                )}
                              </button>
                              
                              {/* RSVP Buttons */}
                              <div className="flex items-center gap-2 ml-auto">
                                <button
                                  onClick={() => handleRSVP(event.event_id, 'going')}
                                  className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                                    event.user_rsvp === 'going' || event.my_rsvp === 'going'
                                      ? 'bg-green-500/20 text-green-400'
                                      : 'bg-zinc-800 text-zinc-300 hover:bg-green-500/10 hover:text-green-600'
                                  }`}
                                >
                                  <Check className="w-4 h-4" />
                                  Going
                                </button>
                                <button
                                  onClick={() => handleRSVP(event.event_id, 'maybe')}
                                  className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                                    event.user_rsvp === 'maybe' || event.my_rsvp === 'maybe'
                                      ? 'bg-yellow-500/20 text-yellow-400'
                                      : 'bg-zinc-800 text-zinc-300 hover:bg-yellow-500/10 hover:text-yellow-400'
                                  }`}
                                >
                                  Maybe
                                </button>
                                <button
                                  onClick={() => handleRSVP(event.event_id, 'not_going')}
                                  className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                                    event.user_rsvp === 'not_going' || event.my_rsvp === 'not_going'
                                      ? 'bg-red-500/20 text-red-400'
                                      : 'bg-zinc-800 text-zinc-300 hover:bg-red-500/10 hover:text-red-400'
                                  }`}
                                >
                                  <X className="w-4 h-4" />
                                  Can't Go
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Expanded RSVP List */}
                      {isExpanded && (
                        <div className="px-4 pb-4">
                          <RsvpList eventId={event.event_id} />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Event Modal */}
      {showAddEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-800 rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-zinc-700">
              <h3 className="text-lg font-semibold text-white">Add Event</h3>
              <button 
                onClick={() => setShowAddEvent(false)}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            
            <form onSubmit={handleCreateEvent} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-200 mb-1">Event Title *</label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  className="w-full px-4 py-2.5 border border-zinc-700 rounded-xl focus:ring-2 focus:ring-black focus:border-black outline-none"
                  placeholder="e.g., Practice at Main Field"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-200 mb-1">Event Type</label>
                <select
                  value={newEvent.event_type}
                  onChange={(e) => setNewEvent({ ...newEvent, event_type: e.target.value })}
                  className="w-full px-4 py-2.5 border border-zinc-700 rounded-xl focus:ring-2 focus:ring-black focus:border-black outline-none"
                >
                  <option value="practice">Practice</option>
                  <option value="game">Game</option>
                  <option value="meeting">Meeting</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-200 mb-1">Start Time *</label>
                  <input
                    type="datetime-local"
                    value={newEvent.start_time}
                    onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                    className="w-full px-4 py-2.5 border border-zinc-700 rounded-xl focus:ring-2 focus:ring-black focus:border-black outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-200 mb-1">End Time</label>
                  <input
                    type="datetime-local"
                    value={newEvent.end_time}
                    onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })}
                    className="w-full px-4 py-2.5 border border-zinc-700 rounded-xl focus:ring-2 focus:ring-black focus:border-black outline-none"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-200 mb-1">Location</label>
                <input
                  type="text"
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                  className="w-full px-4 py-2.5 border border-zinc-700 rounded-xl focus:ring-2 focus:ring-black focus:border-black outline-none"
                  placeholder="e.g., Main Field, 123 Sports Dr"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-200 mb-1">Notes</label>
                <textarea
                  value={newEvent.notes}
                  onChange={(e) => setNewEvent({ ...newEvent, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-zinc-700 rounded-xl focus:ring-2 focus:ring-black focus:border-black outline-none resize-none"
                  placeholder="Any additional details..."
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddEvent(false)}
                  className="flex-1 px-4 py-2.5 border border-zinc-700 rounded-xl font-medium hover:bg-zinc-900 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-2.5 bg-black text-white rounded-xl font-medium hover:bg-zinc-700 transition-colors disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
